import { lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Main } from "./main";
import SuspenseWrapper from "../components/SuspenseWrapper";
import { MailBoxProvider } from "./mailbox/context/mailbox-context";
import { FileProvider } from "./Library/context/file-context";
import { BlogProvider } from "./blogs/context/blog-context";
import { TopicProvider } from "./blogs/context/topic-context";
import { WriterProvider } from "./blogs/context/writer-context";
import { PaymentsTestingRoute } from "./payments/PaymentsTestingGate";
import { AdminPermission } from "./manageAdmin/AdminUserList.types";

// Lazy-loaded page components for code-splitting
const CredentialingPage = lazy(() => import("./credentialing/CredentialingPage").then(m => ({ default: m.CredentialingPage })));
const AlohaProvisioningPage = lazy(() => import("./alohaProvisioning/AlohaProvisioningPage").then(m => ({ default: m.AlohaProvisioningPage })));
const Login = lazy(() => import("./auth/login/Login").then(m => ({ default: m.Login })));
const Dashboard = lazy(() => import("./dashboard/Dashboard").then(m => ({ default: m.Dashboard })));
const FamilyList = lazy(() => import("./family/list/FamilyList").then(m => ({ default: m.FamilyList })));
const ProviderList = lazy(() => import("./provider/list/ProviderList").then(m => ({ default: m.ProviderList })));
const AdminChat = lazy(() => import("./admin-chat/AdminChat").then(m => ({ default: m.AdminChat })));
const BulletinList = lazy(() => import("./bulletin/list/BulletinList").then(m => ({ default: m.BulletinList })));
const AuthRequestList = lazy(() => import("./authRequests/list/AuthRequestList").then(m => ({ default: m.AuthRequestList })));
const ClientReferralList = lazy(() => import("./clientReferrals/list/ClientReferralList").then(m => ({ default: m.ClientReferralList })));
const AdminUserList = lazy(() => import("./manageAdmin/list/AdminUserList").then(m => ({ default: m.AdminUserList })));
const FormList = lazy(() => import("./forms/list/FormList").then(m => ({ default: m.FormList })));
const WaitlistList = lazy(() => import("./waitlist/list/WaitlistList").then(m => ({ default: m.WaitlistList })));
const CandidSyncList = lazy(() => import("./candidSync/list/CandidSyncList").then(m => ({ default: m.CandidSyncList })));
const FeedbackList = lazy(() => import("./feedback/list/FeedbackList").then(m => ({ default: m.FeedbackList })));
const ContactSubmissionsList = lazy(() => import("./contactSubmissions/list/ContactSubmissionsList").then(m => ({ default: m.ContactSubmissionsList })));
const BlogList = lazy(() => import("./blogs/list/BlogList"));
const Library = lazy(() => import("./Library/Library").then(m => ({ default: m.Library })));
const FinancialToolkitList = lazy(() => import("./financialToolkit/list/FinancialToolkitList").then(m => ({ default: m.FinancialToolkitList })));
const ClaimsList = lazy(() => import("./claims/ClaimsList").then(m => ({ default: m.ClaimsList })));
const NotificationManagementList = lazy(() => import("./notifications/NotificationManagementList").then(m => ({ default: m.NotificationManagementList })));
const Halo = lazy(() => import("./halo/Halo").then(m => ({ default: m.Halo })));
const McpAuthorize = lazy(() => import("./mcp/McpAuthorize").then(m => ({ default: m.McpAuthorize })));
const TreatmentPlanAuditor = lazy(() => import("./treatmentPlanAuditor/TreatmentPlanAuditor").then(m => ({ default: m.TreatmentPlanAuditor })));
const TreatmentAuthorizationQueuePage = lazy(() => import("./treatmentAuthorization/queue/TreatmentAuthorizationQueuePage").then(m => ({ default: m.TreatmentAuthorizationQueuePage })));
const TreatmentAuthorizationReviewerPage = lazy(() => import("./treatmentAuthorization/reviewer/TreatmentAuthorizationReviewerPage").then(m => ({ default: m.TreatmentAuthorizationReviewerPage })));
const ManageAuditChecklists = lazy(() => import("./manageAuditChecklists/ManageAuditChecklists").then(m => ({ default: m.ManageAuditChecklists })));
const EditAuditChecklist = lazy(() => import("./manageAuditChecklists/EditAuditChecklist").then(m => ({ default: m.EditAuditChecklist })));
const ImportChecklists = lazy(() => import("./manageAuditChecklists/ImportChecklists").then(m => ({ default: m.ImportChecklists })));
const WeeklyPaymentsPage = lazy(() => import("./payments/WeeklyPaymentsPage").then(m => ({ default: m.WeeklyPaymentsPage })));
const ContentEngineQueuePage = lazy(() => import("./contentEngine/queue/ContentEngineQueuePage").then(m => ({ default: m.ContentEngineQueuePage })));
const ContentEngineReviewPage = lazy(() => import("./contentEngine/review/ContentEngineReviewPage").then(m => ({ default: m.ContentEngineReviewPage })));
const PaymentsOverviewPage = lazy(() => import("./payments/PaymentsOverviewPage").then(m => ({ default: m.PaymentsOverviewPage })));
const PracticeTermsAdvancesPage = lazy(() => import("./payments/PracticeTermsAdvancesPage").then(m => ({ default: m.PracticeTermsAdvancesPage })));

const RoutesList = () => {
  return (
    <Routes>
      <Route path="/">
        <Route path="login" element={<SuspenseWrapper><Login /></SuspenseWrapper>} />
        <Route path="mcp/authorize" element={<SuspenseWrapper><McpAuthorize /></SuspenseWrapper>} />
      </Route>
      <Route element={<Main />}>
        <Route path="/credentialing" element={<SuspenseWrapper><CredentialingPage /></SuspenseWrapper>} />
        <Route path="/credentialing/:caseId" element={<SuspenseWrapper><CredentialingPage /></SuspenseWrapper>} />
        <Route
          path="/dashboard"
          element={
            <SuspenseWrapper>
              <MailBoxProvider>
                <Dashboard />
              </MailBoxProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/clients"
          element={
            <SuspenseWrapper>
              <FamilyList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/client-referrals"
          element={
            <SuspenseWrapper>
              <FileProvider>
                <ClientReferralList />
              </FileProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/providers"
          element={
            <SuspenseWrapper>
              <ProviderList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/messages"
          element={
            <SuspenseWrapper>
              <MailBoxProvider>
                <AdminChat />
              </MailBoxProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/bulletin"
          element={
            <SuspenseWrapper>
              <MailBoxProvider>
                <BulletinList />
              </MailBoxProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/surveys"
          element={
            <SuspenseWrapper>
              <MailBoxProvider>
                <FormList />
              </MailBoxProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/waitlist"
          element={
            <SuspenseWrapper>
              <WaitlistList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/feedback"
          element={
            <SuspenseWrapper>
              <FeedbackList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/contact-submissions"
          element={
            <SuspenseWrapper>
              <ContactSubmissionsList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/library"
          element={
            <SuspenseWrapper>
              <FileProvider>
                <Library />
              </FileProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/blogs"
          element={
            <SuspenseWrapper>
              <BlogProvider>
                <TopicProvider>
                  <WriterProvider>
                    <BlogList />
                  </WriterProvider>
                </TopicProvider>
              </BlogProvider>
            </SuspenseWrapper>
          }
        />
        <Route path="/aloha-provisioning" element={<SuspenseWrapper><AlohaProvisioningPage /></SuspenseWrapper>} />
        <Route path="/aloha-provisioning/:caseId" element={<SuspenseWrapper><AlohaProvisioningPage /></SuspenseWrapper>} />
        <Route
          path="/content-engine"
          element={
            <SuspenseWrapper>
              <ContentEngineQueuePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/content-engine/:workflowId"
          element={
            <SuspenseWrapper>
              <ContentEngineReviewPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/auth-requests"
          element={
            <SuspenseWrapper>
              <FileProvider>
                <AuthRequestList />
              </FileProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/candid-sync"
          element={
            <SuspenseWrapper>
              <CandidSyncList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/claims"
          element={
            <SuspenseWrapper>
              <ClaimsList />
            </SuspenseWrapper>
          }
        />
        <Route path="/payments" element={<Navigate to="/payments/overview" replace />} />
        <Route
          path="/payments/overview"
          element={
            <PaymentsTestingRoute>
              <SuspenseWrapper>
                <PaymentsOverviewPage />
              </SuspenseWrapper>
            </PaymentsTestingRoute>
          }
        />
        <Route
          path="/payments/weekly"
          element={
            <PaymentsTestingRoute>
              <SuspenseWrapper>
                <WeeklyPaymentsPage />
              </SuspenseWrapper>
            </PaymentsTestingRoute>
          }
        />
        <Route
          path="/payments/terms-and-advances"
          element={
            <PaymentsTestingRoute
              requiredPermission={[
                AdminPermission.READ_PAYMENT_CLOSE,
                AdminPermission.MANAGE_PAYMENT_ADVANCE_REQUESTS,
                AdminPermission.MANAGE_PAYMENT_TERMS,
                AdminPermission.MANAGE_PAYMENT_ADVANCES,
                AdminPermission.ADJUST_PAYMENT_ADVANCE_BALANCE,
              ]}
            >
              <SuspenseWrapper>
                <PracticeTermsAdvancesPage />
              </SuspenseWrapper>
            </PaymentsTestingRoute>
          }
        />
        <Route
          path="/advances-concessions"
          element={<Navigate to="/payments/terms-and-advances" replace />}
        />
        <Route path="/mercury-payments" element={<Navigate to="/payments/weekly" replace />} />
        <Route
          path="/manage-admin"
          element={
            <SuspenseWrapper>
              <FileProvider>
                <AdminUserList />
              </FileProvider>
            </SuspenseWrapper>
          }
        />
        <Route
          path="/financial-toolkit"
          element={
            <SuspenseWrapper>
              <FinancialToolkitList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/manage-notifications"
          element={
            <SuspenseWrapper>
              <NotificationManagementList />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/halo"
          element={
            <SuspenseWrapper>
              <Halo />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/treatment-plan-auditor"
          element={
            <SuspenseWrapper>
              <TreatmentPlanAuditor />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/treatment-plan-auditor/runs/:id"
          element={
            <SuspenseWrapper>
              <TreatmentPlanAuditor />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/treatment-authorizations"
          element={
            <SuspenseWrapper>
              <TreatmentAuthorizationQueuePage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/treatment-authorizations/:jobId/review"
          element={
            <SuspenseWrapper>
              <TreatmentAuthorizationReviewerPage />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/manage-audit-checklists"
          element={
            <SuspenseWrapper>
              <ManageAuditChecklists />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/manage-audit-checklists/import"
          element={
            <SuspenseWrapper>
              <ImportChecklists />
            </SuspenseWrapper>
          }
        />
        <Route
          path="/manage-audit-checklists/:id"
          element={
            <SuspenseWrapper>
              <EditAuditChecklist />
            </SuspenseWrapper>
          }
        />

        <Route
          index
          element={
            // if loggged in redirect to dashboard
            <Navigate to="/login" />
          }
        />
      </Route>
    </Routes>
  );
};
export default RoutesList;
